// Music manager - playlist creation, track requesting, and queue management

import { useRef, useState, useCallback, RefObject, useEffect } from 'react';
import { useRoomConfig } from './useRoomConfig';
import { useRoomSocket } from './useRoomSocket';
import {
  generateCommandId,
  createLogger,
  getSongWithContextById,
  getAllSongs,
  getSongMetadata,
  type SongMetadata,
} from '@airhead-games/shared/utilities';
import {
  MediaActionType,
  MediaType,
  SenderRoleType,
  MediaReadyEvent,
  Song,
} from '@airhead-games/shared/types';

const logger = createLogger('useMediaMusicManager');

interface PlaylistTrack extends Song {
  musicUrl?: string;
  albumImageUrl?: string;
  mediaId?: string;
}

export function useMediaMusicManager(
  audioRef: RefObject<HTMLAudioElement | null>
) {
  // --- Basic state ---
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [musicTrackCurrent, setMusicTrackCurrent] = useState<Song | null>(null);
  const [musicTrackCurrentMetadata, setMusicTrackCurrentMetadata] =
    useState<SongMetadata | null>(null);
  const [musicReadyToPlay, setMusicReadyToPlay] = useState(false);

  // --- Simple display state ---
  const [shouldShowMusicDisplay, setShouldShowMusicDisplay] = useState(false);
  const displayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Playlist ---
  const musicPlaylist = useRef<PlaylistTrack[]>([]);
  const musicPlaylistCurrentIndex = useRef<number>(-1);
  const nextTrackRequested = useRef<boolean>(false);
  const currentlyPlayingTrackId = useRef<string | null>(null);

  // --- Caches ---
  const cachedMusicUrls = useRef<
    Record<string, { musicUrl: string; albumImageUrl?: string }>
  >({});

  // --- Contexts ---
  const { roomCode } = useRoomConfig();
  const { sendCommand } = useRoomSocket();

  // Helper to send music-related media commands
  const sendMusicCommand = useCallback(
    (action: MediaActionType, track: PlaylistTrack) => {
      if (!roomCode || !track.mediaId) return;

      sendCommand({
        id: generateCommandId(),
        type: 'MediaElement',
        action,
        mediaType: MediaType.MUSIC,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        content: track.id,
        mediaId: track.mediaId,
        musicUrl: track.musicUrl,
        imageUrl: track.albumImageUrl,
      });
    },
    [roomCode, sendCommand]
  );

  // --- Show display for duration ---
  const showDisplay = useCallback((duration: number) => {
    setShouldShowMusicDisplay(true);

    // Clear existing timer
    if (displayTimeoutRef.current) {
      clearTimeout(displayTimeoutRef.current);
    }

    // Set new timer
    displayTimeoutRef.current = setTimeout(() => {
      setShouldShowMusicDisplay(false);
      displayTimeoutRef.current = null;
      logger.debug(`Display hidden after ${duration}ms`);
    }, duration);
  }, []);

  // --- Play current track ---
  const playCurrentTrack = useCallback(() => {
    const idx = musicPlaylistCurrentIndex.current;
    const track = musicPlaylist.current[idx];
    if (!track || !track.musicUrl || !audioRef.current) return;

    // Prevent playing the same track multiple times
    if (currentlyPlayingTrackId.current === track.id) {
      logger.debug(`Skipping track because it's already playing`);
      return;
    }

    logger.debug('Starting new track...');
    currentlyPlayingTrackId.current = track.id;

    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // Start new track
    audioRef.current.src = track.musicUrl;
    audioRef.current.currentTime = 0;
    audioRef.current.loop = false;
    // Volume is managed by VolumeManager

    audioRef.current
      .play()
      .catch((err) => logger.error('Audio play failed', err));

    // Update state
    setIsMusicPlaying(true);
    setMusicTrackCurrent(track);

    // Set metadata
    const baseMetadata = getSongMetadata(track.id);
    const finalMetadata = {
      ...baseMetadata,
      albumImageUrl: track.albumImageUrl || baseMetadata.albumImageUrl,
    };
    setMusicTrackCurrentMetadata(finalMetadata);

    // Reset flags
    nextTrackRequested.current = false;

    // Show display for 10 seconds when track starts
    showDisplay(10000);

    const context = getSongWithContextById(track.id);
    const artistName = context?.artist?.name || 'Unknown Artist';
    logger.info(
      `Now playing: ${track.title} by ${artistName} from the album ${context?.album?.name}`
    );
    logger.debug(
      `Track ${idx + 1}/${musicPlaylist.current.length} in playlist`
    );
  }, [audioRef, showDisplay]);

  // --- Request track from backend ---
  const requestTrack = useCallback(
    (track: PlaylistTrack) => {
      logger.debug('Requesting track...');

      if (!roomCode || !track) {
        logger.debug('Missing info for MediaRequest');
        return;
      }

      const command = {
        id: generateCommandId(),
        type: 'MediaElement' as const,
        action: MediaActionType.REQUEST,
        mediaType: MediaType.MUSIC,
        senderRole: SenderRoleType.HOST,
        clientTimestamp: Date.now(),
        roomCode,
        musicUrl: track.path,
        imageUrl: getSongMetadata(track.id).albumImageUrl,
        content: track.id,
      };

      logger.debug(`Sent REQUESTED command for ${track.title}`);
      sendCommand(command);
    },
    [roomCode, sendCommand]
  );

  // --- Get next track index ---
  const getNextTrackIndex = useCallback(() => {
    const playlist = musicPlaylist.current;
    const currentIdx = musicPlaylistCurrentIndex.current;
    if (playlist.length === 0) return -1;
    if (currentIdx === -1) return 0;
    return (currentIdx + 1) % playlist.length;
  }, []);

  // --- Monitor for end-of-song display ---
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    function handleTimeUpdate() {
      if (
        !audio?.duration ||
        isNaN(audio.duration) ||
        !musicTrackCurrent ||
        !musicTrackCurrentMetadata
      )
        return;

      const timeRemaining = audio.duration - audio.currentTime;

      // Preload next track when 15s remaining (gives image time to load)
      if (timeRemaining <= 15 && timeRemaining > 0) {
        const nextIdx = getNextTrackIndex();
        if (nextIdx !== -1) {
          const nextTrack = musicPlaylist.current[nextIdx];
          if (
            nextTrack &&
            !cachedMusicUrls.current[nextTrack.id] &&
            !nextTrackRequested.current
          ) {
            requestTrack(nextTrack);
            nextTrackRequested.current = true;
          }
        }

        // Trigger MusicDisplay for last 5s if not already showing
        if (!shouldShowMusicDisplay && musicTrackCurrent) {
          setShouldShowMusicDisplay(true);
        }
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [
    audioRef,
    musicTrackCurrent,
    musicTrackCurrentMetadata,
    getNextTrackIndex,
    requestTrack,
    shouldShowMusicDisplay,
  ]);

  // --- Handle STARTED and COMPLETED commands with proper audio events ---
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    function handlePlaying() {
      const currentIdx = musicPlaylistCurrentIndex.current;
      const currentTrack = musicPlaylist.current[currentIdx];
      if (currentTrack) {
        sendMusicCommand(MediaActionType.STARTED, currentTrack);
        logger.debug('Sent music STARTED command');
      }
    }

    function handleTrackEnded() {
      const currentIdx = musicPlaylistCurrentIndex.current;
      const currentTrack = musicPlaylist.current[currentIdx];
      if (currentTrack) {
        sendMusicCommand(MediaActionType.COMPLETED, currentTrack);
        logger.debug('Sent music COMPLETED command');
      }
    }

    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('ended', handleTrackEnded);

    return () => {
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('ended', handleTrackEnded);
    };
  }, [audioRef, sendMusicCommand]);

  // --- Advance to next track on ended ---
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    function handleEnded() {
      logger.debug('Current track ended, transitioning to next track...');

      const nextIdx = getNextTrackIndex();
      if (nextIdx === -1) return;

      musicPlaylistCurrentIndex.current = nextIdx;
      const nextTrack = musicPlaylist.current[nextIdx];
      if (!nextTrack) return;

      // Reset tracking
      nextTrackRequested.current = false;
      currentlyPlayingTrackId.current = null;

      // Play next track
      if (cachedMusicUrls.current[nextTrack.id] || nextTrack.musicUrl) {
        if (cachedMusicUrls.current[nextTrack.id] && !nextTrack.musicUrl) {
          const cached = cachedMusicUrls.current[nextTrack.id];
          nextTrack.musicUrl = cached.musicUrl;
          nextTrack.albumImageUrl = cached.albumImageUrl;
        }
        playCurrentTrack();
      } else {
        requestTrack(nextTrack);
      }
    }

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [audioRef, getNextTrackIndex, playCurrentTrack, requestTrack]);

  // --- Create playlist ---
  const createMusicPlaylist = useCallback(
    async (
      criteria: Partial<SongMetadata>,
      trackKeys?: string[]
    ): Promise<number> => {
      // Reset everything
      musicPlaylist.current = [];
      musicPlaylistCurrentIndex.current = -1;
      setIsMusicPlaying(false);
      setMusicTrackCurrent(null);
      setMusicTrackCurrentMetadata(null);
      setShouldShowMusicDisplay(false);
      setMusicReadyToPlay(false);

      // Clear the music URL cache to prevent memory accumulation
      Object.values(cachedMusicUrls.current).forEach((url) => {
        try {
          if (url.musicUrl.startsWith('blob:')) {
            URL.revokeObjectURL(url.musicUrl);
          }
        } catch {
          // Ignore revoke of an already-revoked or invalid blob URL during playlist reset.
        }
      });
      cachedMusicUrls.current = {};

      if (displayTimeoutRef.current) {
        clearTimeout(displayTimeoutRef.current);
        displayTimeoutRef.current = null;
      }

      // Build playlist
      const tracksToUse = trackKeys || getAllSongs().map((song) => song.id);
      // Shuffle using crypto.getRandomValues for better randomness
      const getRandomInt = (max: number): number => {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return array[0] % (max + 1);
      };
      for (let i = tracksToUse.length - 1; i > 0; i--) {
        const j = getRandomInt(i);
        [tracksToUse[i], tracksToUse[j]] = [tracksToUse[j], tracksToUse[i]];
      }

      const playlist: PlaylistTrack[] = [];
      for (const trackKey of tracksToUse) {
        const metadata = getSongMetadata(trackKey);
        const matches = Object.entries(criteria).every(([key, value]) => {
          if (value === undefined || value === null) return true;
          return metadata[key as keyof typeof metadata] === value;
        });
        if (matches) {
          const song = getSongWithContextById(trackKey)?.song;
          if (song) playlist.push({ ...song });
        }
      }

      musicPlaylist.current = playlist;
      if (playlist.length > 0) {
        musicPlaylistCurrentIndex.current = 0;
        requestTrack(playlist[0]);
      }

      logger.debug(`Created playlist with ${playlist.length} tracks`);
      return playlist.length;
    },
    [requestTrack]
  );

  // --- Update track with signed URL ---
  const updateTrackWithSignedUrl = useCallback(
    (event: MediaReadyEvent) => {
      const { musicUrl, imageUrl, content: contentId, mediaId } = event;
      if (!musicUrl) {
        logger.warn('No musicUrl in MediaReady event');
        return;
      }
      // Find matching track in playlist (prefer ID match if available)
      let targetTrack = null;
      let targetTrackIndex = -1;

      if (contentId) {
        for (let i = 0; i < musicPlaylist.current.length; i++) {
          const track = musicPlaylist.current[i];
          if (track && track.id === contentId) {
            targetTrack = track;
            targetTrackIndex = i;
            break;
          }
        }
      }

      // Fallback to matching by path substring if no contentId match
      if (!targetTrack) {
        for (let i = 0; i < musicPlaylist.current.length; i++) {
          const track = musicPlaylist.current[i];
          if (track && musicUrl.includes(track.path)) {
            targetTrack = track;
            targetTrackIndex = i;
            break;
          }
        }
      }

      if (!targetTrack) {
        logger.debug('No matching track found for musicUrl', { musicUrl });
        return;
      }

      // Update track with signed URLs
      targetTrack.musicUrl = musicUrl;
      targetTrack.albumImageUrl = imageUrl;
      targetTrack.mediaId = mediaId;

      cachedMusicUrls.current[targetTrack.id] = {
        musicUrl: musicUrl,
        albumImageUrl: imageUrl,
      };

      logger.debug('Updated track in cache with signed URL');

      // If this is the current track and we're not already playing,
      // mark music as ready to play and let MediaContext handle starting playback
      const currentIdx = musicPlaylistCurrentIndex.current;
      if (targetTrackIndex === currentIdx && !isMusicPlaying) {
        setMusicReadyToPlay(true);
        logger.debug(
          'Current track ready, MediaContext will handle playback when audio context available'
        );
      }
    },
    [isMusicPlaying]
  );

  // Flush all music manager state for room transitions
  const flushMusicManager = useCallback(() => {
    logger.debug('Flushing music manager state...');
    // Stop and clear audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.currentTime = 0;
    }
    // Clear all state
    musicPlaylist.current = [];
    musicPlaylistCurrentIndex.current = -1;
    currentlyPlayingTrackId.current = null;
    nextTrackRequested.current = false;
    setMusicTrackCurrent(null);
    setMusicTrackCurrentMetadata(null);
    setShouldShowMusicDisplay(false);
    setIsMusicPlaying(false);
    setMusicReadyToPlay(false);
    // Clear any pending timeouts
    if (displayTimeoutRef.current) {
      clearTimeout(displayTimeoutRef.current);
      displayTimeoutRef.current = null;
    }
    logger.debug('Music manager flushed');
  }, [audioRef]);

  const playMusicPlaylist = useCallback(async () => {
    const currentIdx = musicPlaylistCurrentIndex.current;
    const currentTrack = musicPlaylist.current[currentIdx];

    // If we have a cached track ready to play, play it
    if (currentTrack && currentTrack.musicUrl && !isMusicPlaying) {
      playCurrentTrack();
    } else if (currentIdx >= 0) {
      // We have a playlist but no cached URL yet - normal flow will handle when MediaReady arrives
      logger.debug('Playlist ready but waiting for MediaReady event...', {
        currentIdx,
        playlistLength: musicPlaylist.current.length,
        hasCurrentTrack: !!currentTrack,
        hasMusicUrl: !!currentTrack?.musicUrl,
        isMusicPlaying,
      });
    }
  }, [playCurrentTrack, isMusicPlaying]);

  // --- Cleanup ---
  useEffect(() => {
    const urlsAtMount = { ...cachedMusicUrls.current };
    return () => {
      Object.values(urlsAtMount).forEach((url) => {
        try {
          if (url.musicUrl.startsWith('blob:')) {
            URL.revokeObjectURL(url.musicUrl);
          }
        } catch {
          // Ignore revoke of an already-revoked or invalid blob URL during unmount.
        }
      });
      if (displayTimeoutRef.current) clearTimeout(displayTimeoutRef.current);
    };
  }, []);

  return {
    isMusicPlaying,
    musicTrackCurrent,
    musicTrackCurrentMetadata,
    shouldShowMusicDisplay,
    currentDisplayTrackId: musicTrackCurrent?.id || null,
    hasMusicPlaylist:
      musicPlaylist.current.length > 0 &&
      musicPlaylistCurrentIndex.current >= 0,
    musicReadyToPlay,
    createMusicPlaylist,
    playMusicPlaylist,
    updateTrackWithSignedUrl,
    flushMusicManager,
  };
}
